import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import type { MultiSelectOption } from "./MultiSelectFilter";

interface SearchableMultiSelectFilterProps {
  label?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  width?: number;
  showSelectAllClearAll?: boolean;
  dense?: boolean;
}

// Shrinks the closed control to the height/font of an inline table filter row.
const denseSx = {
  "& .MuiOutlinedInput-root": { height: 34 },
  "& .MuiSelect-select": {
    fontSize: "0.75rem",
    minHeight: "auto",
    height: "100%",
    boxSizing: "border-box",
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiChip-root": { height: 20, fontSize: "0.7rem" },
};

export default function SearchableMultiSelectFilter({
  label,
  options,
  value,
  onChange,
  placeholder = "All",
  searchPlaceholder = "Search…",
  width = 220,
  showSelectAllClearAll = false,
  dense = false,
}: SearchableMultiSelectFilterProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Snapshot of the selection when the menu opened, so the "selected first"
  // ordering stays stable while open (rows don't jump when you toggle them).
  const [pinnedSelected, setPinnedSelected] = useState<string[]>([]);
  const clearable = value.length > 0;
  const labelId = label ? `${label.replace(/\s+/g, "-").toLowerCase()}-search-multi-label` : undefined;
  const labelByValue = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => (o.searchText ?? o.label).toLowerCase().includes(q)) : options;
  }, [options, query]);

  const pinnedSet = useMemo(() => new Set(pinnedSelected), [pinnedSelected]);
  const selectedOpts = filtered.filter((o) => pinnedSet.has(o.value));
  const restOpts = filtered.filter((o) => !pinnedSet.has(o.value));

  const handleSelectAll = () => onChange(filtered.map((o) => o.value));
  const handleClearAll = () => onChange([]);

  const handleOpen = () => {
    setPinnedSelected(value);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  const renderOption = (opt: MultiSelectOption) => (
    <MenuItem key={opt.value} value={opt.value} sx={{ pl: 1 }}>
      <Checkbox checked={value.includes(opt.value)} size="small" sx={{ mr: 1, p: 0.5 }} />
      <ListItemText primary={opt.label} slotProps={{ primary: { noWrap: true } }} />
    </MenuItem>
  );

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <Box component="span" sx={{ color: "text.secondary" }}>{placeholder}</Box>;
    }
    const [first, ...rest] = selected;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Chip label={labelByValue.get(first) ?? first} size="small" sx={{ minWidth: 0 }} />
        {rest.length > 0 && (
          <Box component="span" sx={{ whiteSpace: "nowrap", color: "text.secondary", flexShrink: 0 }}>
            +{rest.length}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <FormControl size="small" sx={{ width, ...(dense ? denseSx : {}) }}>
      {label && <InputLabel id={labelId} shrink>{label}</InputLabel>}
      <Select<string[]>
        labelId={labelId}
        multiple
        displayEmpty
        value={value}
        sx={clearable ? { "& .MuiSelect-select": { pr: "52px !important" } } : undefined}
        input={
          <OutlinedInput
            notched={!!label}
            label={label}
            endAdornment={
              clearable ? (
                <IconButton
                  size="small"
                  aria-label="Clear"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  sx={{ position: "absolute", right: 26, p: 0.25, color: "action.active" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null
            }
          />
        }
        renderValue={renderValue}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          onChange(typeof v === "string" ? v.split(",") : v);
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        MenuProps={{
          autoFocus: false,
          PaperProps: { sx: { maxHeight: 360, width: Math.max(width, 320) } },
        }}
      >
        <ListSubheader sx={{ p: 1, lineHeight: "normal", bgcolor: "background.paper" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key !== "Escape") e.stopPropagation();
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {showSelectAllClearAll && (
            <Box
              display="flex"
              justifyContent="space-between"
              gap={1}
              sx={{ mt: 0.5 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                sx={{ minWidth: 0, px: 1, textTransform: "none" }}
              >
                Select all
              </Button>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                sx={{ minWidth: 0, px: 1, textTransform: "none" }}
              >
                Clear all
              </Button>
            </Box>
          )}
        </ListSubheader>
        {selectedOpts.map(renderOption)}
        {selectedOpts.length > 0 && restOpts.length > 0 && <Divider component="li" />}
        {restOpts.map(renderOption)}
        {filtered.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No matches" />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
